<template>
	<v-row>
		<v-col>
			<add-place
					v-model="formIsValid.addPlace"
					:partner="partner"
					:pass="pass"
					:places="localPlaces"
					:user="user"
					@add-place="addPlace"
			>
				<template v-slot:button>
					<v-btn
							v-if="pass.id"
							class="mt-1 rounded-lg"
							color="secondary"
							depressed
							:disabled="!hasExistingPlaces"
							outlined
							small
							@click="addExisting"
					>
						<v-icon>
							mdi-plus
						</v-icon>
						{{ $vuetify.breakpoint.mdAndUp ? 'From ' : '' }}My Network
					</v-btn>
				</template>
			</add-place>

			<form-divider/>

			<added-places
					:allow-sorting="!!pass?.id"
					:partner="partner"
					:pass="pass"
					:places="localPlaces"
					@edit-place="editPlace"
					@remove-place="removePlace"
					@save-sort="saveSort"
			/>

			<save-form
					v-if="this.pass.id"
					:refs="$refs"
					:is-saving="isSaving"
					:is-valid="!!localPlaces.length"
					new-forms
					save-text="Continue"
					@cancel-action="exit"
					@save-action="next"
			></save-form>

			<!-- Add Existing -->
			<bw-modal
					v-if="dialog.showAddExisting"
					title="Add Places From My Network"
					:width="1200"
			>
				<template #body>
					<v-row>
						<v-col>
							<bw-table
									v-model="selectedExistingPlaces"
									aria-label="data-table"
									:custom-search="customFilterFunction()"
									dense
									disable-pagination
									:display-filters="displayFilters"
									force-selectable-row
									:headers="existingPlaceHeaders"
									hide-default-footer
									hide-row-select
									:items="availablePartnerPlaces"
									key="availablePlacesKey"
									:showSearch="false"
							>
								<template v-slot:item.name="{ item }">
									<div class="text-subtitle-2">
										{{ item.name }}
									</div>
									<div v-if="item?.overwrittenNames?.length > 0" class="text-subtitle-3 font-italic">
										<div v-for="(overwrittenName) in item.overwrittenNames" :key="`overwritten-name-${overwrittenName}`">
											{{ overwrittenName }}
										</div>
									</div>
								</template>
							</bw-table>
						</v-col>
					</v-row>
				</template>
				<template #footer>
					<div class="align-center d-flex justify-space-between w-100">
						<div></div>
						<div class="d-flex justify-end">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									text
									@click="dialog.showAddExisting = false;"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:disabled="!selectedExistingPlaces.length"
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									@click="addSelectedExistingPlaces"
							>
								Add
							</v-btn>
						</div>
					</div>
				</template>
			</bw-modal>
			<!-- Add Existing - END -->

			<!-- Editing Place -->
			<bw-modal
					v-if="editingPlace"
					drawerOnDesktop
					:width="800"
			>
				<template #body>
					<div class="ma-n4 ma-md-n8">
						<v-row
								class="position-absolute w-100 z-2"
								noGutters
						>
							<v-col cols="12">
								<PlaceMap
										v-model="editingPlace.geometry.location"
										:height="$vuetify.breakpoint.smAndDown ? 200 : 240"
										:draggable="isManuallyAddedPoi(editingPlace)"
										noRounding
										@dragend="updateEditingLocation"
								/>
							</v-col>
							<v-col v-if="$vuetify.breakpoint.smAndUp" cols="12">
								<bw-scroll-tabs
										active-class="text--darken-2"
										background-color="white"
										class="position-relative z-2"
										color="secondary darken-2"
										contentRef="editPlaceTabContent"
										dark
										fixed-tabs
										:offsetY="$vuetify.breakpoint.xsOnly ? 200 : 288"
										:show-arrows="false"
								>
									<v-tab class="secondary--text" href="#editPlaceDetails">
										Details
									</v-tab>
									<v-tab
											v-if="!!pass?.id"
											class="secondary--text"
											href="#editPlaceCustomize"
									>
										Customize
									</v-tab>
									<v-tab v-if="!hideCheckIns" class="secondary--text"
												 href="#editPlaceCheckIn">
										Check-In
									</v-tab>
									<v-tab class="secondary--text" href="#editPlaceContacts">
										Contacts
									</v-tab>
									<v-tab class="secondary--text" href="#editBusinessHours">
										Business Hours
									</v-tab>
								</bw-scroll-tabs>
								<v-divider class="position-relative z-1"/>
							</v-col>
						</v-row>

						<bw-scroll-tabs-content ref="editPlaceTabContent">

							<v-row
									:style="{ marginTop: $vuetify.breakpoint.xsOnly ? '200px' : '288px' }"
							>
								<v-col cols="12">

									<template v-if="isManuallyAddedPoi(editingPlace)">
										<div class="align-center d-flex mt-n4 mb-4 mt-md-n8 mb-md-8">
											<v-icon color="error">mdi-map-marker</v-icon>
											<div class="ps-2">Move the map marker to update location.</div>
										</div>
									</template>

									<v-form
											v-model="formIsValid.editPlace"
											ref="editPlaceForm"
											class="mt-n4"
											eager
									>
										<ui-box eager new-forms fluid :form="$refs.editPlaceForm">

											<form-title anchor="editPlaceDetails" title="Place Details"/>
											<form-divider inner/>

											<v-row v-if="!isManuallyAddedPoi(editingPlace)">
												<v-col>
													<v-row>
														<v-col>
															<v-text-field
																	v-model="editingPlace.name"
																	class="rounded-lg"
																	:class="{ required: !pass?.id }"
																	clearable
																	:disabled="!!pass?.id"
																	hide-details="auto"
																	label="Place Name"
																	outlined
																	:readonly="!!pass?.id"
																	:rules="rules.placeName"
																	type="text"
															></v-text-field>
														</v-col>
													</v-row>
													<v-row>
														<v-col>
															<div class="text-body-2 grey--text text--darken-1">
																<div v-if="editingPlace.formatted_address"
																		 class="d-flex mt-2">
																	<v-icon class="mr-2" size="18">mdi-map-marker</v-icon>
																	<span>{{ editingPlace.formatted_address }}</span>
																</div>
																<div v-if="editingPlace.website" class="d-flex mt-2">
																	<v-icon class="mr-2" size="18">mdi-web</v-icon>
																	<a :href="editingPlace.website"
																		 style="width: calc(100% - 24px)">
																		{{ editingPlace.website }}
																	</a>
																</div>
																<div v-if="editingPlace.formatted_phone_number"
																		 class="d-flex mt-2">
																	<v-icon class="mr-2" size="18">mdi-cellphone</v-icon>
																	<a :href="`tel:${editingPlace.formatted_phone_number}`">
																		{{ editingPlace.formatted_phone_number }}
																	</a>
																</div>
															</div>
														</v-col>
													</v-row>
												</v-col>
											</v-row>

											<form-divider v-if="!isManuallyAddedPoi(editingPlace)" inner/>

											<v-row v-if="isManuallyAddedPoi(editingPlace)">
												<v-col>
													<v-text-field
															v-model="editingPlace.name"
															class="rounded-lg"
															:class="{ required: !pass?.id }"
															clearable
															:disabled="!!pass?.id"
															hide-details="auto"
															label="Place Name"
															outlined
															:readonly="!!pass?.id"
															type="text"
													></v-text-field>
												</v-col>
											</v-row>

											<v-row v-if="isManuallyAddedPoi(editingPlace)">
												<v-col v-if="!!editingAddress">
													<v-autocomplete
															v-model="selectedAddress"
															class="rounded-lg"
															:filter="() => true"
															hide-details="auto"
															:items="addressSuggestions"
															item-text="description"
															item-value="editingPlace_id"
															label="Address"
															outlined
															return-object
															:search-input.sync="searchAddress"
															@input="getAddressDetails"
													>
														<template v-slot:append>
															<v-btn
																	color="error"
																	depressed
																	fab
																	outlined
																	small
																	@click="() => {
																selectedAddress = null;
																addressSuggestions = [];
																editingPlace.formatted_address = editingAddress.address;
																editingPlace.geometry.location = editingAddress.location;
																editingAddress = null;
															}"
															>
																<v-icon style="top: 0 !important;">
																	mdi-close
																</v-icon>
															</v-btn>
														</template>
													</v-autocomplete>
												</v-col>
												<template v-if="!editingAddress">
													<v-col>
														<v-text-field
																class="rounded-lg"
																hide-details="auto"
																label="Address"
																outlined
																readonly
																:value="editingPlace.formatted_address"
														>
															<template v-slot:append>
																<v-btn
																		color="secondary"
																		depressed
																		fab
																		outlined
																		small
																		@click="editingAddress = { address: editingPlace.formatted_address, location: editingPlace.geometry.location }"
																>
																	<v-icon style="top: 0 !important;">
																		mdi-pencil-outline
																	</v-icon>
																</v-btn>
															</template>
														</v-text-field>
													</v-col>
												</template>
											</v-row>

											<v-row v-if="isManuallyAddedPoi(editingPlace)">
												<v-col>
													<v-text-field
															v-model.number="editingPlace.geometry.location.lat"
															class="rounded-lg"
															hide-details="auto"
															label="Latitude"
															outlined
													></v-text-field>
												</v-col>
												<v-col>
													<v-text-field
															v-model.number="editingPlace.geometry.location.lng"
															class="rounded-lg"
															hide-details="auto"
															label="Longitude"
															outlined
													></v-text-field>
												</v-col>
											</v-row>

											<v-row v-if="isManuallyAddedPoi(editingPlace)">
												<v-col cols="12" sm="6" md="12" lg="6">
													<v-text-field
															v-model="editingPlace.website"
															class="flex-grow-1 rounded-lg"
															hide-details="auto"
															label="Website"
															outlined
															:rules="rules.website"
													></v-text-field>
												</v-col>
												<v-col cols="12" sm="6" md="12" lg="6">
													<vue-tel-input-vuetify
															v-model="editingPlace.formatted_phone_number"
															ref="placePhone"
															append-icon="mdi-menu-down"
															class="flex-grow-1 rounded-lg"
															clearable
															:defaultCountry="'us'"
															hide-details="auto"
															label="Phone"
															mode="international"
															outlined
															:preferred-countries="['us', 'ca']"
															:rules="rules.placePhone"
															@keydown="phoneKeyEvent"
															@validate="isPlacePhoneValid"
													></vue-tel-input-vuetify>
												</v-col>
											</v-row>

											<!-- PTG MGMT - Venue Image -->
											<template v-if="!pass?.id">
												<v-row>
													<v-col>
														<v-row>
															<v-col>
																<div
																		class="align-center field-actions d-flex justify-end">

																	<div
																			class="grey--text ms-2 text-caption text--darken-1"
																	>
																		<template v-if="isGeneratingDescription">
																			This may take a couple seconds.
																		</template>
																		<template
																				v-else-if="disableAutoGeneratingDescription">
																			Auto-generate is currently unavailable.
																		</template>
																		<template
																				v-else-if="!editingPlace.name || !editingPlace.formatted_address">
																			Auto-generate: Name and address required.
																		</template>
																		<template v-else>Auto-generate</template>
																	</div>
																	<v-btn
																			aria-label="Auto-Generate Description"
																			icon
																			:disabled="!editingPlace.name || !editingPlace.formatted_address || disableAutoGeneratingDescription"
																			:loading="isGeneratingDescription"
																			@click="generateDescription"
																	>
																		<v-icon
																				v-if="!editingPlace.name || !editingPlace.formatted_address || disableAutoGeneratingDescription"
																				key="autogen-off"
																		>
																			mdi-autorenew-off
																		</v-icon>
																		<v-icon
																				v-else
																				key="autogen-on"
																				color="secondary"
																		>
																			mdi-autorenew
																		</v-icon>
																	</v-btn>
																</div>
																<wysiwyg-input
																		v-model="editingPlace.description"
																		:counter="placeDescriptionMaxLength"
																		:disabled="isGeneratingDescription"
																		hide-details="auto"
																		label="Description"
																		outlined
																		rows="3"
																></wysiwyg-input>
															</v-col>
														</v-row>

														<ui-box class="mt-8" border-color="rgba(0, 0, 0, 0.38)">
															<v-row>
																<v-col>
																	<div class="v-text-field__details">
																		<div
																				class="v-messages theme--light"
																				role="alert"
																		>
																			<div class="v-messages__wrapper">
																				<div
																						class="v-messages__message message-transition-enter-to"
																				>
																					<div class="mb-1" style="font-size: 12px;">
																						<span class="black--text">Image</span>
																						<span
																								class="text--darken-2 grey--text mt-2 mb-2 d-block"
																						>
																							Recommended minimum size: 1280 x 640 @ 2X
																						</span>
																					</div>
																				</div>
																			</div>
																		</div>
																	</div>
																</v-col>
															</v-row>

															<v-row>
																<template v-if="editingPlace.media?.[headerIndex]">
																	<bw-image
																			v-model="editingPlace.media[headerIndex]"
																			:key="`media-${editingPlace.media[headerIndex].id}`"
																			aspect-ratio="2"
																			cover
																			show-remove
																			upload-assignment="venue"
																			:upload-assignment-id="editingPlace.id"
																			@delete="onEditingPlaceRemoveImage"
																	></bw-image>
																</template>
															</v-row>

														</ui-box>
													</v-col>
												</v-row>
											</template>
											<!-- PTG MGMT - Venue Image - END -->

											<!-- PTG PB - Customization -->
											<template v-else>
												<v-row>
													<v-col>
														<form-title
																anchor="editPlaceCustomize"
																title="Place Customization"
														/>
														<form-divider inner/>

														<v-row>
															<v-col>
																<div class="position-relative">
																	<div class="float-right ms-8 mb-6">
																		<div class="font-weight-medium text-caption">
																			Image
																			<v-divider class="my-2 opacity-50"/>
																		</div>
																		<v-img
																				:aspect-ratio="2"
																				cover
																				class="rounded"
																				lazy-src="https://placehold.co/400x300?text=Loading"
																				max-width="180"
																				min-width="180"
																				:src="editingPlace.media[headerIndex].url"
																		></v-img>
																	</div>
																	<div class="font-weight-medium mb-4 text-caption">
																		Name
																		<v-divider
																				class="my-2 opacity-50"
																				style="width: calc(100% - 180px - 2rem);"
																		/>
																		<span class="font-weight-medium text-body-1">
																{{ editingPlace.name }}
															</span>
																	</div>
																	<div class="font-weight-medium text-caption">
																		Description
																		<v-divider
																				class="my-2 opacity-50"
																				style="width: calc(100% - 180px - 2rem);"
																		/>
																	</div>
																	<span v-html="editingPlace.description"></span>
																</div>
															</v-col>
														</v-row>

														<form-subsection class="mt-8" title="Pass Override">
															Sometimes it may be necessary to override the description,
															image and/or name of a place in your pass. You may do that
															here without overwriting the defaults shown above.
														</form-subsection>

														<v-row>
															<v-col>
																<v-text-field
																		v-model="editingPlace.deal_venue.name"
																		class="rounded-lg"
																		clearable
																		hide-details="auto"
																		label="Place Name"
																		outlined
																		type="text"
																></v-text-field>
															</v-col>
														</v-row>

														<v-row>
															<v-col>
																<div
																		class="align-center field-actions d-flex justify-end">

																	<div
																			class="grey--text ms-2 text-caption text--darken-1"
																	>
																		<template v-if="isGeneratingDescription">
																			This may take a couple seconds.
																		</template>
																		<template
																				v-else-if="disableAutoGeneratingDescription">
																			Auto-generate is currently unavailable.
																		</template>
																		<template
																				v-else-if="!editingPlace.name || !editingPlace.formatted_address">
																			Auto-generate: Name and address required.
																		</template>
																		<template v-else>Auto-generate</template>
																	</div>
																	<v-btn
																			aria-label="Auto-Generate Description"
																			icon
																			:disabled="!editingPlace.name || !editingPlace.formatted_address || disableAutoGeneratingDescription"
																			:loading="isGeneratingDescription"
																			@click="generateDescription"
																	>
																		<v-icon
																				v-if="!editingPlace.name || !editingPlace.formatted_address || disableAutoGeneratingDescription"
																				key="autogen-off"
																		>
																			mdi-autorenew-off
																		</v-icon>
																		<v-icon
																				v-else
																				key="autogen-on"
																				color="secondary"
																		>
																			mdi-autorenew
																		</v-icon>
																	</v-btn>
																</div>
																<wysiwyg-input
																		v-model="editingPlace.deal_venue.description"
																		:counter="placeDescriptionMaxLength"
																		:disabled="isGeneratingDescription"
																		hide-details="auto"
																		label="Description"
																		outlined
																		required
																		rows="3"
																></wysiwyg-input>
															</v-col>
														</v-row>

														<ui-box :class="editingPlace.recentlyAdded ? 'my-8' : 'mt-8'"
																		border-color="rgba(0, 0, 0, 0.38)">
															<v-row>
																<v-col>
																	<div class="v-text-field__details">
																		<div
																				class="v-messages theme--light"
																				role="alert"
																		>
																			<div class="v-messages__wrapper">
																				<div
																						class="v-messages__message message-transition-enter-to"
																				>
																					<div class="mb-1" style="font-size: 12px;">
																						<span class="black--text">Image</span>
																						<span
																								class="text--darken-2 grey--text mt-2 mb-2 d-block"
																						>
																							Recommended minimum size: 1280 x 640 @ 2X
																						</span>
																					</div>
																				</div>
																			</div>
																		</div>
																	</div>
																</v-col>
															</v-row>

															<v-row>
																<template v-if="editingPlace.deal_venue.media?.[dealVenueHeaderIndex]">
																	<bw-image
																			v-model="editingPlace.deal_venue.media[dealVenueHeaderIndex]"
																			:aspect-ratio="2"
																			cover
																			show-remove
																			upload-assignment="dealVenue"
																			:upload-assignment-id="editingPlace.id"
																			@delete="onEditingPlaceRemoveDealVenueImage"
																	></bw-image>
																</template>
															</v-row>

														</ui-box>
													</v-col>
												</v-row>
											</template>
											<!-- PTG PB - Customization - END -->

											<template v-if="!pass?.id || editingPlace.recentlyAdded">
												<v-row>
													<v-col>
														<bw-search-select
																v-model="editingPlace.categories"
																confirm-remove
																hide-details="auto"
																:items="extraData.globalCategories"
																item-text="name"
																item-value="id"
																label="Categories"
																multiple
														/>
													</v-col>
												</v-row>
											</template>
											<template v-if="showAdvancedMedia">
												<v-form
														v-model="formIsValid.video"
														ref="videoForm"
														class="mb-8"
												>
													<ui-box
															border-color="rgba(0, 0, 0, 0.38)"
															:class="editingPlace.recentlyAdded ? 'my-8' : 'mt-8'"
															:form="$refs.videoForm"
															:status="null"
															style="max-width: 738;"
													>
														<form-title
															tag="p"
															title="Video"
															titleClass="black--text caption"
														>
															This video will be viewable on any pass this place is included on.
														</form-title>
														<v-select
																v-model="editingPlace.video.host"
																class="rounded-lg"
																:items="[
																	{ name: 'Youtube Embed URL (Recommended)', value: 'youtube' },
																	{ name: 'Vimeo Embed URL', value: 'vimeo' },
																	{ name: 'Upload File', value: 'cloudinary' }
																]"
																item-text="name"
																label="Video Type"
																outlined
																@change="resetVideo"
														></v-select>
														<form-divider class="mb-8 mt-2"/>
														<template v-if="editingPlace.video.host === 'cloudinary'">
															<v-row no-gutters>
																<v-col class="pb-1">
																	<v-text-field
																			v-model="editingPlace.video.caption"
																			class="rounded-lg"
																			hint=""
																			label="Title"
																			outlined
																			persistent-hint
																	></v-text-field>
																</v-col>
															</v-row>
															<v-row no-gutters>
																<bw-image
																		v-model="editingPlace.video"
																		:auto-show-uploader="false"
																		class="rounded-lg"
																		:cols="{ lg: 6, md: 6, sm: 6 }"
																		enable-tabs="video"
																		show-remove
																		type="video"
																		upload-assignment="venue"
																		:upload-assignment-id="editingPlace.id"
																		@delete="onVideoDelete"
																></bw-image>
															</v-row>
															<p class="caption mt-2">Your file must be an MP4 and must be smaller than 500mb.</p>
														</template>
														<template v-else>
															<v-row no-gutters>
																<v-col class="pb-1">
																	<v-text-field
																			v-model="editingPlace.video.caption"
																			class="rounded-lg"
																			hint=""
																			label="Title"
																			outlined
																			persistent-hint
																	></v-text-field>
																</v-col>
															</v-row>
															<v-row no-gutters>
																<v-col class="pb-1">
																	<v-text-field
																			v-model="editingPlace.video.url"
																			class="rounded-lg"
																			hint=""
																			:label="`${Bandwango.capitalize(editingPlace.video.host)} Embed URL`"
																			outlined
																			persistent-hint
																			:rules="rules.website"
																			@change="
																				delete editingPlace.video.id;
																				delete editingPlace.video.media_id
																			"
																	></v-text-field>
																</v-col>
															</v-row>
														</template>
													</ui-box>
												</v-form>

												<v-form
														ref="audioForm"
														class="mb-8"
												>
													<ui-box
															border-color="rgba(0, 0, 0, 0.38)"
															:class="editingPlace.recentlyAdded ? 'my-8' : 'mt-8'"
															:form="false"
															:status="null"
															style="max-width: 738;"
													>
														<form-title
															tag="p"
															title="Audio Clip"
															titleClass="black--text caption"
														>
															This audio will be viewable on any pass this place is included on.
														</form-title>
														<v-row no-gutters>
															<v-col class="pb-1">
																<v-text-field
																		v-model="editingPlace.audio.caption"
																		class="rounded-lg"
																		hint=""
																		label="Title"
																		outlined
																		persistent-hint
																></v-text-field>
															</v-col>
														</v-row>
														<v-row no-gutters>
															<bw-image
																	v-model="editingPlace.audio"
																	:auto-show-uploader="false"
																	class="rounded-lg"
																	:cols="{ lg: 6, md: 6, sm: 6 }"
																	enable-tabs="audio"
																	show-remove
																	type="audio"
																	upload-assignment="venue"
																	:upload-assignment-id="editingPlace.id"
																	@delete="onAudioDelete"
															></bw-image>
														</v-row>
														<p class="caption mt-2">Your file must be MP3, OGG, WAV, or M4A formats and smaller than 500 mb.</p>
													</ui-box>
												</v-form>
											</template>
											<v-row v-if="enableAdvancedFeatures">
												<v-col>
													<v-btn
															class="mb-8 me-2 me-md-4 rounded-lg"
															color="secondary"
															depressed
															outlined
															small
															@click="showAdvancedMedia = !showAdvancedMedia"
													>
														<v-icon
																:class="{
																	'me-2': $vuetify.breakpoint.smAndUp,
																	'ms-n1': $vuetify.breakpoint.smAndUp
																}"
														>
															mdi-eye-outline
														</v-icon>
														<span>{{ showAdvancedMedia ? 'Hide' : 'Show' }} Advanced</span>
													</v-btn>
												</v-col>
											</v-row>
										</ui-box>
									</v-form>

									<form-divider/>

									<template v-if="!pass.id">
										<div>

											<form-title anchor="editPlaceCheckIn" title="Check-In Info">
												If <span class="font-weight-medium">{{ editingPlace.name }}</span>
												has an included check-in in a pass with validation set to 'Pin'
												the following pin number will be used to validate user's location.
											</form-title>

											<form-divider inner/>

											<template v-if="editingPlace.partner_id !== partner.id">
												<v-alert
														class="mb-8 rounded-lg"
														border="left"
														color="warning"
														dense
														icon="mdi-alert-circle"
														text
												>
											<span class="text-body-2">
												This place is managed by another partner. You may not set or
												update this pin. You may contact the place to have a pin set or
												reach out to your Bandwango Success Specialist.
											</span>
												</v-alert>
											</template>

											<v-row>
												<v-col cols="12" md="6">
													<bw-pin-input
															v-model="editingPlace.pin_number"
															:disabled="editingPlace.partner_id !== partner.id"
															hide-details="auto"
													/>
												</v-col>
											</v-row>

											<v-row>
												<v-col cols="12">
													<qr-code-loader
															:partner-id="partner.id"
															:place-id="editingPlace.id"
															:place-name="editingPlace.name"
													/>
												</v-col>
											</v-row>

											<form-divider my="8"/>

										</div>
									</template>

									<template v-if="pass.id && !hideCheckIns">
										<ui-box
												v-if="!editingPlace.checkIns?.length && enableAdvancedFeatures"
												fluid
												new-forms
										>
											<form-title anchor="editPlaceCheckIn" title="Check-In"
											></form-title>
											<form-divider inner/>

											<template>
												Allow check-ins to this location?
											</template>
										</ui-box>

										<v-form
												v-else
												v-for="(checkIn, idx) in editingPlace.checkIns"
												:key="`checkin-${idx}`"
												v-model="formIsValid.checkIn"
												:ref="`checkInForm-${idx}`"
												class="mt-10"
											>
											<ui-box
													fluid
													new-forms
											>
												<form-title anchor="editPlaceCheckIn" :title="`${ idx > 0 ? 'Bonus ' : '' }Check-In`">
													<template
															v-if="(idx > 0 || pass.template.settings.places_to_check_in?.hide) && enableAdvancedFeatures"
															v-slot:button
													>
														<v-btn
																class="mt-1 rounded-lg"
																color="error"
																depressed
																outlined
																small
																@click="removeBonusCheckIn(idx)"
														>
															<v-icon>mdi-minus</v-icon>
															Remove
														</v-btn>
													</template>
												</form-title>
												<form-divider inner/>
												<template>
													<div>
														<template v-if="checkInIs(idx, 'pin')">
															<v-alert
																	:class="['mt-8', 'rounded-lg', {
																		'mb-4': editingPlace.partner_id !== partner.id,
																		'mb-8': editingPlace.partner_id === partner.id
																	}]"
																	border="left"
																	color="info"
																	dense
																	icon="mdi-information-slab-circle"
																	text
															>
																<span class="text-body-2">
																	Please Note: This same PIN number will be used
																	for all redemptions with a PIN validation across
																	this place.
																</span>
															</v-alert>

															<template v-if="editingPlace.partner_id !== partner.id">
																<v-alert
																		class="mb-8 rounded-lg"
																		border="left"
																		color="warning"
																		dense
																		icon="mdi-alert-circle"
																		text
																>
																	<span class="text-body-2">
																		This place is managed by another partner. You may not set
																		or update this pin. You may contact the place to have a
																		pin set or reach out to your Bandwango Support Specialist.
																	</span>
																</v-alert>
															</template>
														</template>

														<v-row>
															<v-col cols="12" sm="6">
																<v-text-field
																		v-model="checkIn.name"
																		class="rounded-lg required"
																		clearable
																		hide-details="auto"
																		label="Check-In Label"
																		outlined
																		type="text"
																		:rules="rules.checkIn.name"
																></v-text-field>
															</v-col>
															<v-col cols="12" sm="6">
																<v-select
																		v-if="!hasDealCheckInOverride"
																		v-model="checkIn.template_id"
																		class="rounded-lg required"
																		hide-details="auto"
																		:items="checkInTemplates"
																		item-text="name"
																		item-value="id"
																		label="Check-In Validation"
																		outlined
																		@change="updateCheckInType(idx)"
																		:rules="rules.checkIn.validation"
																></v-select>
																<v-text-field
																		v-else
																		class="rounded-lg"
																		disabled
																		hide-details="auto"
																		label="Check-In Validation"
																		outlined
																		readonly
																		:value="dealCheckInType"
																></v-text-field>
															</v-col>
														</v-row>

														<div v-if="checkInIs(idx, 'qr')" class="mb-8">
															<qr-code-loader
																	:partner-id="partner.id"
																	:place-id="editingPlace.id"
																	:place-name="editingPlace.name"
															/>
														</div>

														<v-row>
															<v-col v-if="checkInIs(idx, 'pin')" cols="12" sm="6">
																<bw-pin-input
																		v-model="editingPlace.pin_number"
																		:disabled="editingPlace.partner_id !== partner.id"
																		hide-details="auto"
																/>
															</v-col>
															<v-col v-if="checkInIs(idx, 'gps')" cols="12" sm="6">
																<v-text-field
																		v-model.number="checkIn.redemption_radius"
																		v-two-decimals
																		class="rounded-lg required"
																		hide-details="auto"
																		label="Radius"
																		max="10"
																		min="0.25"
																		outlined
																		persistent-hint
																		suffix=" miles"
																		:rules="rules.checkIn.radius"
																		step="0.25"
																		type="number"
																		@wheel.native.prevent
																></v-text-field>
															</v-col>
														</v-row>

														<template v-if="showAdvancedBonusCheckins[idx]">
															<v-row>
																<v-col>
																	<v-text-field
																		v-model="checkIn.languageTags[checkIn.checkInIdx].value"
																		class="rounded-lg required"
																		hint="Use this field to customize the wording of the button
																			that passholders use to initiate the check-in process."
																		label="Pre Check-In Button Label"
																		outlined
																		persistent-hint
																		:rules="rules.checkInBtn"
																	></v-text-field>
																</v-col>
																<v-col>
																	<v-text-field
																		v-model="checkIn.languageTags[checkIn.checkedInIdx].value"
																		class="rounded-lg required"
																		hint="Use this field to customize the wording of the button
																			that passholders use to finish the check-in process."
																		label="Post Check-In Button Label"
																		outlined
																		persistent-hint
																		:rules="rules.checkInBtn"
																	></v-text-field>
																</v-col>
															</v-row>
															<v-row>
																<v-col>
																	<wysiwyg-input
																		v-model="checkIn.short_information"
																		:counter="1250"
																		hide-details="auto"
																		hint="Shown on the mobile pass."
																		label="Brief Description"
																		outlined
																		rows="3"
																	></wysiwyg-input>
																</v-col>
															</v-row>
															<v-row>
																<v-col>
																	<v-textarea
																			v-model="checkIn.restrictions"
																			auto-grow
																			class="rounded-lg"
																			counter="1250"
																			hide-details="auto"
																			hint="Please list any restrictions here. For example: 'Can only be used on weekends.'"
																			label="Restrictions"
																			outlined
																			rows="2"
																	></v-textarea>
																</v-col>
															</v-row>

															<v-row>
																<v-col>
																	<v-switch
																			v-model="checkIn.repeat"
																			class="mb-2 mt-0"
																			dense
																			hide-details="auto"
																			inset
																			label="Is this a repeat use check-in?"
																	></v-switch>
																</v-col>
															</v-row>
															<v-row v-if="checkIn.repeat">
																<v-col>
																	<v-row>
																		<v-col>
																			<v-text-field
																					v-model.number="checkIn.repeat_delay"
																					class="flex-grow-1 rounded-lg required"
																					clearable
																					hide-spin-buttons
																					label="Repeat Delay"
																					min="1"
																					outlined
																					prefix="Every"
																					:rules="rules.checkIn.repeat_delay"
																					type="number"
																					@wheel.native.prevent
																			></v-text-field>
																		</v-col>
																		<v-col>
																			<v-select
																					v-model="checkIn.repeat_delay_type"
																					class="flex-grow-1 rounded-lg required"
																					clearable
																					:items="['hours', 'days', 'weeks', 'months']"
																					label="Repeat Delay Type"
																					outlined
																					:rules="rules.checkIn.repeat_delay_type"
																			></v-select>
																		</v-col>
																	</v-row>
																</v-col>
															</v-row>
															<v-row>
																<v-col>
																	<blackout-dates
																			:ref="`blackoutDate${idx}`"
																			:refs="$refs"
																			:form-is-valid="formIsValid"
																			:product="checkIn"
																			:show-title="false"
																			:show-add-date="false"
																			:show-no-dates="false"
																			:timezone="partner.timezone"
																	/>
																</v-col>
															</v-row>
														</template>
														<template
															v-if="checkIn && !hideCheckIns"
														>
															<v-btn
																	:key="`adv-btn-${showAdvancedBonusCheckins[idx]}`"
																	class="mt-7 rounded-lg"
																	color="secondary"
																	depressed
																	outlined
																	small
																	@click="toggleAdvancedBonusCheckin(idx)"
															>
																<v-icon :key="`adv-icon-${showAdvancedBonusCheckins[idx]}`">
																	{{
																		showAdvancedBonusCheckins[idx]
																				? 'mdi-eye-off-outline'
																				: 'mdi-eye-outline'
																	}}
																</v-icon>
																{{
																	showAdvancedBonusCheckins[idx] ? 'Hide Advanced' : 'Show Advanced'
																}}
															</v-btn>
														</template>
													</div>
												</template>
											</ui-box>
										</v-form>

										<form-divider style="margin-bottom: 32px !important;"/>

										<template v-if="(!editingPlace.checkIns?.length || pass.template.settings.multiple_check_ins) && enableAdvancedFeatures">
											<div class="d-flex justify-end mb-8">
												<v-btn
														class="rounded-lg"
														color="secondary"
														depressed
														outlined
														small
														@click="addCheckIn"
												>
													<v-icon>mdi-plus</v-icon>
													Add Check-In
												</v-btn>
											</div>
											<form-divider/>
										</template>
									</template>

									<form-title anchor="editPlaceContacts" title="Place Contacts">
										<template v-slot:button>
											<v-btn
													class="mt-1 rounded-lg"
													color="secondary"
													depressed
													outlined
													small
													@click="addAnotherContact"
											>
												<v-icon>mdi-plus</v-icon>
												{{ editingPlace.merchants.length ? 'Add Another' : 'Add' }}
												Contact
											</v-btn>
										</template>
									</form-title>
									<form-divider inner/>

									<template v-if="editingPlace.merchants.length">
										<div
												v-for="(contact, idx) in editingPlace.merchants"
												:key="`contact-${idx}`"
										>
											<v-row>
												<v-col>
													<div class="align-start d-flex">
														<div class="flex-grow-1">
															<div class="align-center d-flex mb-3">
																<h6>
																	<v-icon class="ml-n1" size="26">
																		mdi-account-outline
																	</v-icon>
																	{{
																		contact.name || `${contact.first_name} ${contact.last_name}`
																	}}
																</h6>
																<v-btn
																		v-if="contact.require_new_password === 0"
																		class="ms-2 rounded-pill"
																		color="success"
																		depressed
																		small
																		style="pointer-events: none"
																		text
																>
																	<v-icon class="me-0 ml-n2" size="20">
																		mdi-check-decagram
																	</v-icon>
																	<span
																			class="font-weight-bold text-caption">Verified</span>
																</v-btn>
															</div>
															<div class="text-body-2 grey--text text--darken-1">
																<div v-if="contact.email" class="d-flex mt-2">
																	<v-icon class="mr-2" size="18">mdi-email-outline
																	</v-icon>
																	<a
																			:href="`mailto:${contact.email}`"
																			class="text-body-2"
																	>
																		{{ contact.email }}
																	</a>
																</div>
																<div v-if="contact.phone" class="d-flex mt-2">
																	<v-icon class="mr-2" size="18">mdi-cellphone
																	</v-icon>
																	<a :href="`tel:${contact.phone}`">
																		{{ contact.phone }}
																	</a>
																</div>
																<div v-if="contact.secondary_phone"
																		 class="d-flex mt-2">
																	<v-icon class="mr-2" size="18">mdi-cellphone
																	</v-icon>
																	<a :href="`tel:${contact.secondary_phone}`">
																		{{ contact.secondary_phone }}
																	</a>
																</div>
															</div>
														</div>
														<div class="d-flex pt-2">
															<template v-if="contact.require_new_password !== 0 || isAdmin">
																<v-btn
																		color="secondary"
																		depressed
																		fab
																		outlined
																		small
																		@click="editContact(contact, idx)"
																>
																	<v-icon style="top: 0 !important;">
																		mdi-pencil-outline
																	</v-icon>
																</v-btn>
																<v-divider
																		class="mx-2 my-1"
																		vertical
																></v-divider>
																<v-btn
																		color="error"
																		depressed
																		fab
																		outlined
																		small
																		@click="removeContact(contact, idx)"
																>
																	<v-icon>
																		mdi-trash-can-outline
																	</v-icon>
																</v-btn>
															</template>
														</div>
													</div>
												</v-col>
											</v-row>
											<form-divider mb="4" inner/>
										</div>
									</template>

									<template v-else>
										<ui-box>
											<div
													class="align-center d-flex justify-center"
													style="height: 24px;"
											>
												Oops, no contacts yet! Add one
												<a
														class="ms-1"
														href="javascript:void(0)"
														@click="addAnotherContact"
												>here</a>.
											</div>
										</ui-box>
									</template>

						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<ui-box

								:eager="!!editingPlace.id"
								new-forms
							>

								<form-title anchor="editBusinessHours" title="Business Hours">
									<template v-if="editingPlace.place_id">Hours are pulled from the Merchant’s
										Google Business Profile.
										To refresh the business hours from the Google listing,
										click on the Refresh Business Hours button below.
									</template>
									<template v-else>
										Because this is a manually created Place,
										you may update the business hours for this listing.
										Updating here will reflect on any live passes this Place is on.
									</template>
								</form-title>

								<form-divider inner/>

								<business-hours
									place-id-field="place_id"
									:update-endpoint="`${endpoints.getBusinessHours}${editingPlace.id}`"
									:venue="editingPlace">
								</business-hours>

							</ui-box>
								</v-col>
							</v-row>

						</bw-scroll-tabs-content>
					</div>
				</template>
				<template #footer>
					<div class="align-center d-flex justify-space-between w-100">
						<div></div>
						<div class="d-flex justify-end">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									text
									@click="cancelUpdatePlaceDetails"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:disabled="
										!formIsValid.editPlace ||
										!formIsValid.checkIn ||
										!formIsValid.video
									"
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									@click="doEditPlace"
							>
								Save
							</v-btn>
						</div>
					</div>
				</template>
			</bw-modal>
			<!-- Editing Place - END -->

			<!-- Remove Place -->
			<bw-modal v-if="removingPlace" title="Remove Place">
				<template #body>
					Are you sure you want to remove <b>{{ removingPlace.name }}</b> from your
					{{ pass?.id ? 'pass' : 'network' }}?

					<v-alert
							class="mb-0 mt-8 rounded-lg"
							border="left"
							color="warning"
							dense
							icon="mdi-alert-circle"
							text
					>
						<span class="text-body-2">
							<span class="font-weight-bold">Warning: </span>
							<template v-if="pass?.id">
								This will remove this place and any inventory from this pass.
							</template>
							<template v-else-if="!!removingPlace.passes?.length">
								This will only remove the place from your network, but not from any existing passes this place is in. Here is a list of passes that this place is currently in:
							</template>
							<template v-else>
								This will remove this place from your network.
							</template>
						</span>
					</v-alert>
					<template v-if="!!removingPlace.passes?.length">

						<ul class="no-bullets mt-6 mb-6 ml-4">
							<li v-for="inPass in removingPlace.passes">
								<a class="text-decoration-none" :href="`/partner-portal/${partner.id}/passes/${inPass.id}/places`" target="_blank">{{ inPass.name }}</a>
							</li>
						</ul>

					</template>

				</template>
				<template #footer>
					<div class="align-center d-flex justify-end w-100">
						<v-btn
								class="me-2 me-md-4 rounded-lg"
								color="grey darken-1"
								depressed
								:large="$vuetify.breakpoint.mdAndUp"
								:loading="isSaving"
								text
								@click="cancelRemovePlace"
						>
							Cancel
						</v-btn>
						<v-btn
								class="rounded-lg"
								color="primary"
								depressed
								:large="$vuetify.breakpoint.mdAndUp"
								:loading="isSaving"
								@click="doRemovePlace"
						>
							Remove
						</v-btn>
					</div>
				</template>
			</bw-modal>
			<!-- Remove Place - END -->

			<!-- Editing Contact -->
			<bw-modal v-if="editingContact" max-width="calc(100dvw - 6rem)">
				<template #body>
					<v-form
							v-model="formIsValid.editContact"
							ref="editContactForm"
							class="mt-n4"
							id="asdf"
					>
						<ui-box
								fluid
								:form="$refs.editContactForm"
								new-forms
						>

							<form-title :title="`${ isNewContact ? 'New' : 'Edit'} Contact`"/>
							<form-divider inner/>

							<v-row>
								<v-col cols="12" sm="6">
									<v-text-field
											v-model="editingContact.first_name"
											ref="contactFirstName"
											class="rounded-lg"
											hide-details="auto"
											label="First Name"
											outlined
									></v-text-field>
								</v-col>
								<v-col cols="12" sm="6">
									<v-text-field
											v-model="editingContact.last_name"
											class="rounded-lg"
											hide-details="auto"
											label="Last Name"
											outlined
									></v-text-field>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-text-field
											v-model="editingContact.email"
											ref="contactEmail"
											class="flex-grow-1 rounded-lg"
											hide-details="auto"
											label="Email"
											outlined
											:rules="rules.contactEmail"
											@input="validateContactPhone"
									></v-text-field>
								</v-col>
							</v-row>
							<v-row>
								<v-col cols="12" sm="6">
									<vue-tel-input-vuetify
											v-model="editingContact.phone"
											ref="contactPhone"
											append-icon="mdi-menu-down"
											class="flex-grow-1 rounded-lg"
											clearable
											:defaultCountry="'us'"
											hide-details="auto"
											label="Phone"
											mode="international"
											outlined
											:preferred-countries="['us', 'ca']"
											:rules="rules.contactPhone"
											@input="validateContactEmail"
											@keydown="phoneKeyEvent"
											@validate="isContactPhoneValid"
									></vue-tel-input-vuetify>
								</v-col>
								<v-col cols="12" sm="6">
									<vue-tel-input-vuetify
											v-model="editingContact.secondary_phone"
											append-icon="mdi-menu-down"
											class="flex-grow-1 rounded-lg"
											clearable
											:defaultCountry="'us'"
											hide-details="auto"
											label="Secondary Phone"
											mode="international"
											outlined
											:preferred-countries="['us', 'ca']"
											@keydown="phoneKeyEvent"
									></vue-tel-input-vuetify>
								</v-col>
							</v-row>
						</ui-box>
					</v-form>
				</template>
				<template #footer>
					<div class="align-center d-flex justify-space-between w-100">
						<div></div>
						<div class="d-flex justify-end">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									text
									@click="cancelEditContact"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:disabled="!formIsValid.editContact && !checkNewContactValid()"
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									@click="doEditContact"
							>
								Save
							</v-btn>
						</div>
					</div>
				</template>
			</bw-modal>
			<confirm-dialog
					:open="!!errorDescription"
					:description="errorDescription"
					dismissable
					title=""
					@dismiss="errorDescription = null"
			></confirm-dialog>
			<!-- Editing Contact - END -->

			<!-- Remove Contact -->
			<bw-modal
					v-if="removingContact"
					title="Remove Contact"
					max-width="calc(100dvw - 6rem)"
			>
				<template #body>
					Are you sure you want to delete the contact for
					<b>{{
							removingContact.name || `${removingContact.first_name} ${removingContact.last_name}`
						}}</b>?
				</template>
				<template #footer>
					<div class="align-center d-flex justify-space-between w-100">
						<div></div>
						<div class="d-flex justify-end">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									text
									@click="cancelRemoveContact"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									@click="doRemoveContact"
							>
								Remove
							</v-btn>
						</div>
					</div>
				</template>
			</bw-modal>
			<!-- Remove Contact - END -->

			<!-- Set Default Check-In Template ID -->
			<bw-modal
					v-if="dialog.setDefaultCheckInTemplateId"
					title="Default Check-In Method"
					max-width="calc(100dvw - 6rem)"
			>
				<template #body>

					<template v-if="!hasDealCheckInOverride">
						Your <span class="font-weight-bold">{{ localPass.template.name }}</span>
						will contain check-ins to your places. Here you can set the default
						validation type for places that will be onboarded.
					</template>
					<template v-else>
						Your <span class="font-weight-bold">{{ localPass.template.name }}</span>
						will contain <span class="font-weight-bold">{{ dealCheckInType }}</span>
						check-ins to your places. This validation method has been set as an
						override on the pass level.
					</template>

					<form-divider/>

					<v-row>
						<v-col cols=“12”>
							<v-select
									v-if="!hasDealCheckInOverride"
									v-model="localPass.pass_settings.default_checkin_template_id"
									class="rounded-lg required"
									hide-details="auto"
									:items="checkInTemplates"
									item-text="name"
									item-value="id"
									label="Check-In Validation"
									outlined
							></v-select>
							<v-text-field
									v-else
									class="rounded-lg"
									disabled
									hide-details="auto"
									label="Check-In Validation"
									outlined
									readonly
									:value="dealCheckInType"
							></v-text-field>
						</v-col>
					</v-row>

				</template>
				<template #footer>
					<div class="align-center d-flex justify-space-between w-100">
						<div></div>
						<div class="d-flex justify-end">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									text
									@click="cancelDefaultCheckIn"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isSaving"
									@click="saveDefaultCheckIn"
							>
								Save
							</v-btn>
						</div>
					</div>
				</template>
			</bw-modal>
			<!-- Set Default Check-In Template ID - END -->

		</v-col>
	</v-row>
</template>

<script>
import AddPlace from '../../../Places/Components/AddPlace.vue';
import AddedPlaces from '../../../Places/Components/AddedPlaces.vue';
import ConfirmDialog from '@app/Shared/UI/ConfirmDialog.vue';
import BlackoutDates from "@app/Shared/Components/BlackoutDates.vue";
import {BwPlace, helpers, Place, PlaceholderMedia} from '../../../helpers';
import {searchHelpers} from "../../../searchHelpers";
import FormDivider from "../../../UI/Form/FormDivider.vue";
import BusinessHours from "../../../Components/BusinessHours.vue";
import QrCodeLoader from '../../../Components/QrCodeLoader.vue';

const emptyAudio = { title: '', subtitle: '', host: 'cloudinary', location: 'audio' };
const emptyVideo = { caption: '', host: 'youtube', location: 'video', url: ''};

export default {
	name: "Places",
	components: {
		AddPlace,
		AddedPlaces,
		BlackoutDates,
		BusinessHours,
		ConfirmDialog,
		FormDivider,
		QrCodeLoader
	},
	props: {
		categories: {
			type: Array,
			default: () => []
		},
		checkInTemplates: {
			type: Array,
			default: () => []
		},
		extraData: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object,
			default: () => ({})
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		places: {
			type: Array,
			default: () => []
		},
		user: {
			type: Object,
			default: () => ({})
		},
		venueAdminRoleId: [String, Number]
	},
	data() {
		const { latitude, longitude } = this.partner;
		const initialLocation = latitude && longitude
				? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
				: { lat: null, lng: null };

		return {
			addressSuggestions: [],
			dialog: {
				setDefaultCheckInTemplateId: false,
				showAddExisting: false
			},
			isGeneratingDescription: false,
			disableAutoGeneratingDescription: false,
			placeDescriptionMaxLength: 5000,
			defaultCheckInTemplateId: this.pass.pass_settings?.default_checkin_template_id,
			displayFilters: {
				deals: {
					name: 'Passes',
					type: 'select',
					data: this.extraData.passes,
					position: 'main',
					clearable: true,
					filterOn: 'deal_ids',
				},
				categories: {
					name: 'Categories',
					type: 'select',
					data: this.extraData.venueCategories,
					position: 'main',
					multiselect: true,
					filterOn: 'category_ids',
				},
			},
			editingAddress: false,
			editingContact: null,
			editingContactIdx: null,
			editingContactPhoneIsValid: true,
			editingPlace: null,
			editingPlaceIdx: null,
			editingPlacePhoneIsValid: true,
			editingPlacePinIsValid: true,
			errorDescription: null,
			existingPlaceHeaders: [
				{
					align: 'start',
					selected: true,
					text: 'Name',
					value: 'name',
				},
				{
					align: 'start',
					selected: true,
					text: 'Address',
					value: 'address'
				},
			],
			formIsValid: {
				addPlace: false,
				checkIn: !this.pass?.id || (this.pass.template?.settings?.places_to_check_in?.hide || false),
				editPlace: false,
				editContact: false,
				video: false
			},
			initialLocation,
			isNewContact: false,
			isSaving: false,
			localPass: {},
			localPlaces: [],
			removingContact: null,
			showAdvancedBonusCheckins: [],
			removingContactIdx: null,
			removingPlace: null,
			removingPlaceIdx: null,
			rules: {
				checkIn: {
					name: [v => !!v || 'Check-in label is required'],
					radius: [v => !!v || 'Check-in radius is required'],
					validation: [v => !!v || 'Check-in validation type is required'],
					repeat_delay: [
						// 0 is not allowed, so !!v is suitable here
						v => !isNaN(v) && v > 0 || 'Repeat delay must be greater than 0.',
						v => !!v || 'Repeat delay is required.',
						v => !isNaN(v) || 'Repeat delay must be a number.'
					],
					repeat_delay_type: [
						v => !!v || 'Repeat delay type is required.'
					]
				},
				checkInBtn: [
					v => !!v || 'Button Label is required.'
				],
				contactEmail: [
					v => v !== this.user.email || 'You cannot enter your own email as the contact.',
					v => {
						const pattern =
								// eslint-disable-next-line max-len
								/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
						if(!v) {
							if(!this.editingContact.phone) {
								return 'Please enter a valid email or phone.';
							}
							else {
								return true;  // phone is entered, no need for email
							}
						}
						return pattern.test(v) || 'Please enter a valid email.';
					}
				],
				contactPhone: [
					v => {
						if(!v) {
							if(!this.editingContact.email) {
								return 'Please enter a valid phone or email.';
							}
							else {
								return true;  // email is entered, no need for phone
							}
						}
						return this.editingContactPhoneIsValid || 'Please enter a valid phone.';
					}
				],
				firstName: [
					v => !!v || 'First name is required.'
				],
				placeName: [
					v => !!v || 'Place name is required.'
				],
				placePhone: [
					v => !v || (!!v && this.editingPlacePhoneIsValid) || 'Please enter a valid phone.'
				],
				radius: [
					v => !!v || 'Mile radius is required',
					v => (v >= 0.25) || 'Mile radius cannot be less than 0.25 miles',
					v => (v <= 10) || 'Mile radius cannot be more than 10 miles'
				],
				website: [
					// v => !!v || 'Website is required.',
					v => {
						const protocolRegex = /^https?:\/\//;
						return (!v || (!!v && protocolRegex.test(v))) || 'Please include the whole website url including http(s)://'
					},
					v => {
						const fullRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-a-zA-Z0-9()@:%_+.~#?&\/=]*$/;
						return (!v || (!!v && fullRegex.test(v))) || 'Website url is invalid.'
					}
				]
			},
			showAdvancedMedia: false,
			searchAddress: '',
			selectedAddress: null,
			selectedExistingPlaces: [],
			suggestions: [],
		};
	},
	mounted() {
		this.localPass = cloneDeep({
			...this.pass,
			pass_settings: {
				default_checkin_template_id: null,
				...this.pass.pass_settings
			}
		});

		// Deep clone places so we don't mutate the prop
		cloneDeep(this.places).forEach(place => this.localPlaces.push(new Place(place)));
	},
	computed: {
		availablePartnerPlaces() {
			return this.partner.places?.filter(place => !this.localPlaces.some(p => p.id === place.id)) ?? [];
		},

		dealCheckInType() {
			const { checkInTemplates, pass } = this;
			if([null, '', 'by_product'].includes(pass?.check_in_validation)) return null;
			return checkInTemplates
					.find(template => template.redemption_validation === pass.check_in_validation)
					.name;
		},

		editDialogWidth() {
			if(this.$vuetify.breakpoint.lgAndUp) return 1000;
			if(this.$vuetify.breakpoint.mdAndUp) return 800;
			return null;
		},

		enableAdvancedFeatures() {
			return this.isAdmin || this.$bwFeatures.eb_advanced_features;
		},

		endpoints() {
			if(this.pass?.id) {
				return {
					add: `/partner-portal/${this.partner.id}/passes/add-venue-to-pass/${this.pass.id}`,
					addExisting: `/partner-portal/${this.partner.id}/passes/add-existing-venues-to-pass/${this.pass.id}`,
					getVenue: `/partner-portal/${this.partner.id}/passes/get-venue/${this.pass.id}/`,
					refreshVenues: `/partner-portal/${this.partner.id}/passes/get-deal-venues/${this.pass.id}/`,
					remove: `/partner-portal/${this.partner.id}/passes/remove-venue-from-pass/`,
					update: `/partner-portal/${this.partner.id}/passes/update-pass-venue/`,
					generateDescription: `/partner-portal/${this.partner.id}/places/generate-description/`,
					getPasses: `/partner-portal/${this.partner.id}/places/get-venue-passes/`,
					getBusinessHours: `/partner-portal/${this.partner.id}/places/get-business-hours/`,
				};
			} else {
				return {
					add: `/partner-portal/${this.partner.id}/places/add-place-to-partner`,
					addExisting: `/partner-portal/${this.partner.id}/places/add-existing-places-to-Partner`,
					getVenue: `/partner-portal/${this.partner.id}/places/get-venue/`,
					remove: `/partner-portal/${this.partner.id}/places/remove-place-from-partner/`,
					update: `/partner-portal/${this.partner.id}/places/update-partner-place/`,
					generateDescription: `/partner-portal/${this.partner.id}/places/generate-description/`,
					getPasses: `/partner-portal/${this.partner.id}/places/get-venue-passes/`,
					getBusinessHours: `/partner-portal/${this.partner.id}/places/get-business-hours/`,
				};
			}
		},

		hasDealCheckInOverride() {
			return ![null, 'by_product'].includes(this.localPass?.check_in_validation ?? null);
		},

		hasExistingPlaces() {
			return this.availablePartnerPlaces.length > 0;
		},

		hideCheckIns() {
			const settings = this.pass.template?.settings || {};
			const hide = settings.places_to_check_in?.hide;
			const multiple = settings.multiple_check_ins;

			return multiple === true ? false : !!hide;
		},

		dealVenueHeaderIndex() {
			const {
				editingPlace: { deal_venue: { media = [] } = {} }
			} = this;

			return media.findIndex( x => x.location === 'Header');
		},

		headerIndex() {
			const {
				editingPlace: { media = [] }
			} = this;

			return media.findIndex( x => x.location === 'Header');
		},

		isAdmin() {
			return !!this.user?.is_admin;
		},

		shouldHideCheckIns() {
			return this.pass?.template?.settings?.check_in_validation?.hide;
		},

		videoIndex() {
			return this.editingPlace.media.findIndex( x => x.location === 'video');
		}
	},
	methods: {
		addAnotherContact() {
			const place = this.editingPlace;
			this.isNewContact = true;
			this.editingContact = new Contact(place, this.venueAdminRoleId);
			this.editingContactIdx = place.merchants.length;
		},
		addCheckIn() {
			const { checkInTemplates, defaultCheckInTemplateId, editingPlace, pass } = this;

			// If we do not have a default check-in template we need to set one
			if(!defaultCheckInTemplateId && !this.hasDealCheckInOverride) {
				return this.dialog.setDefaultCheckInTemplateId = true;
			}

			const checkInTemplate = checkInTemplates.find(
				template =>
					this.hasDealCheckInOverride
						? template.redemption_validation === pass.check_in_validation
						: template.id === defaultCheckInTemplateId
			);

			const languageTags = [ ...editingPlace.partnerLanguageTags ];

			const len = editingPlace.checkIns.push({
				checkedInIdx: this.getLanguageTagIndex('CHECKEDIN', { languageTags }),
				checkInIdx: this.getLanguageTagIndex('CHECKIN', { languageTags }),
				id: 0,
				languageTags,
				name: 'Bonus Check-In',
				redemption_blackout_dates: [],
				redemption_radius: pass.redemption_radius || 0.25,
				redemption_validation: checkInTemplate.redemption_validation,
				repeat: 0,
				repeat_delay: 1,
				repeat_delay_type: 'months',
				restrictions: null,
				short_information: '',
				template_id: checkInTemplate.id
			});

			this.showAdvancedBonusCheckins.push(false);

			this.updateCheckInType(len - 1);
		},

		checkNewContactValid() {
			const contactFirstNameValid = this.$refs.contactFirstName
					? this.$refs.contactFirstName.internalValue && this.$refs.contactFirstName.valid
					: false;
			const contactEmailValid = this.$refs.contactEmail
					? this.$refs.contactEmail.internalValue && this.$refs.contactEmail.valid
					: false;
			return contactFirstNameValid && (contactEmailValid || this.editingContactPhoneIsValid);
		},

		customFilterFunction() {
			return searchHelpers.searchFieldsAndAdditionals(this.existingPlaceHeaders, ['overwrittenNames']);
		},

		generateDescription() {
			this.errors = {};

			if(Object.keys(this.errors).length) {
				window.EventBus.$emit('system-messages', [
					{
						message: 'You must enter a name and address.',
						messageType: 'error'
					}
				]);
				return false;
			}

			this.isGeneratingDescription = true;

			axios.put(this.endpoints.generateDescription, {
				name: this.editingPlace.name,
				address: this.editingPlace.formatted_address,
				maxLength: this.placeDescriptionMaxLength
			})
					.then(response => {
						let { data } = response;

						if(data.length > this.placeDescriptionMaxLength) {
							data = data.substring(0, this.placeDescriptionMaxLength);
						}

						if(this.pass?.id) {
							this.editingPlace.deal_venue.description = data;
						}
						else {
							this.editingPlace.description = data;
						}

						this.isGeneratingDescription = false;
					})
					.catch(err => {
						console.error('Auto-Generate Description Error: ', err);
						this.isGeneratingDescription = false;
						this.disableAutoGeneratingDescription = true;
					});
		},

		addExisting() {
			this.dialog.showAddExisting = true;

			// this.modals.addExisting = BwModalProps({
			// 	title: 'Places',
			// 	entity: this.availablePartnerPlaces,
			// 	cancel: () => {
			// 		this.modals.addExisting = {}
			// 	},
			// 	confirm: this.addSelectedExistingPlaces,
			// 	disabled: !this.selectedExistingPlaces.length,
			// 	width: 1200
			// });
		},

		async addPlace({ place: newPlace, manual }) {
			const exists = !manual && this.places
					.some(existingPlace => existingPlace.google_place_id === newPlace.place_id);
			// This should not happen, but just in case
			if(manual || !exists) {
				try {
					newPlace.skipAIDescription = true;
					if(manual)
						delete newPlace.place_id;
					const { data: { data: place = null } } = await axios.post(
							this.endpoints.add,
							new BwPlace(newPlace, true)
					);
					if(place) {
						//add the new place to the local places if it doesn't already exist (check id)
						if(!this.localPlaces.some(p => p.id === place.id)) {
							this.localPlaces.unshift(new Place(place));

							this.saveSort();
							// update side menu
							this.updateSecondaryMenu();
						}
						// Open the edit modal
						this.editPlace({ place: this.localPlaces[0], idx: 0 }, true);
					}
				}
				catch (error) {
					console.error('Error adding place:', error);
				}
			}
		},

		// To emit multiple data points we must pass it as a single object
		// We can either destructure here or use a single arg, but in either case we need to
		// use the keys that are passed, in this case: place, manual. In the case below we've
		// renamed an arg to newPlace to differentiate it from the place in the loop.
		async addSelectedExistingPlaces() {
			this.isSaving = true;

			try {
				const venueIds = this.selectedExistingPlaces.map(place => place.id);

				await axios.post(
						this.endpoints.addExisting,
						{ venueIds }
				);
				this.selectedExistingPlaces.forEach(place => {
					this.localPlaces.unshift(new Place(place));
				});

				this.saveSort();

				// reset
				this.isSaving = false;
				this.selectedExistingPlaces = [];
				this.dialog.showAddExisting = false;

				// update side menu
				this.updateSecondaryMenu();

				// Get Venues
				try {
					// Get all pass venues fresh
					const { data: { data: places = null } } = await axios.get(
							this.endpoints.refreshVenues, {
								params: {
									orderBy: 'sortOrder',
									withOverwrittenNames: true,
									withQrFlag: true,
									withPinFlag: true,
								}
							}
					);

					this.localPlaces = [];
					places.forEach(place => this.localPlaces.push(new Place(place)));
				}
				catch (error) {
					console.error('Error getting places:', error);
				}
			}
			catch (error) {
				console.error('Error adding place:', error);
			}
		},

		cancelDefaultCheckIn() {
			this.dialog.setDefaultCheckInTemplateId = false;
		},

		cancelEditContact() {
			this.isNewContact = false;
			this.editingContact = null;
			this.editingContactIdx = null;
			this.editingContactPhoneIsValid = true;
			this.formIsValid.editContact = false;
			this.isSaving = false;
		},

		cancelEditPlace() {
			this.editingAddress = false;
			this.selectedAddress = null;
			this.addressSuggestions = [];
			this.editingPlace = null;
			this.editingPlaceIdx = null;
			this.editingPlacePinIsValid = true;
			this.editingPlacePhoneIsValid = true;
			this.formIsValid.editPlace = true;
			this.formIsValid.video = true;
			this.isSaving = false;
			this.isGeneratingDescription = false;
			this.disableAutoGeneratingDescription = false;
			this.showAdvancedBonusCheckins.fill(false);
			this.showAdvancedMedia = false;
		},

		cancelRemoveContact() {
			this.removingContact = null;
			this.removingContactIdx = null;
			this.isSaving = false;
		},

		cancelRemovePlace() {
			this.removingPlace = null;
			this.removingPlaceIdx = null;
			this.isSaving = false;
		},

		cancelUpdatePlaceDetails() {
			this.editingAddress = false;
			this.selectedAddress = null;
			this.addressSuggestions = [];
			this.editingPlace = null;
			this.editingPlaceIdx = null;
			this.formIsValid.editPlace = true;
			this.formIsValid.video = true;
			this.isGeneratingDescription = false;
			this.disableAutoGeneratingDescription = false;
			this.showAdvancedBonusCheckins.fill(false);
			this.showAdvancedMedia = false;
		},

		checkInIs(idx, type) {
			const checkIn = this.editingPlace.checkIns[idx];

			return checkIn.redemption_validation.toLowerCase().includes(type);
		},

		async doEditContact() {
			const {
				editingContact,
				editingContactIdx,
				editingPlace,
				editingPlaceIdx,
				isNewContact,
				localPlaces
			} = this;
			this.isSaving = true;
			let url;
			let method;
			const params = { fullVenues: true };
			editingContact.current_venue_id = editingPlace.id;
			if(!isNewContact) {

				// Not deleting these leads to errors when saving
				delete editingContact.venues;
				delete editingContact.pivot;

				method = 'put';
				url = editingContact.type === 'user'
						? `/users/${editingContact.id}`
						: `/venues/${editingPlace.id}/contacts/${editingContact.id}`;
			}
			else {
				method = 'post';
				url = editingContact.email ? `/users` : `/venues/${editingPlace.id}/contacts`;
				params.defaultRole = true;
				params.emptyDefaultName = true
			}

			try {

				const { data: newContact = null } = await axios({
					method: method,
					url,
					data: this.editingContact,
					params,
				});

				// splice the response place back in
				// We must update both because the first is the real place and the other is a clone
				localPlaces[editingPlaceIdx].merchants.splice(editingContactIdx, 1, newContact);
				editingPlace.merchants.splice(editingContactIdx, 1, newContact);
				this.cancelEditContact();
			}
			catch (error) {
				this.errorDescription = error?.response?.data?.message || 'Error adding contact';
			}
			finally {
				this.cancelEditContact();
			}
		},

		async doEditPlace() {
			const { editingPlace, editingPlaceIdx, endpoints, localPlaces } = this;
			this.isSaving = true;

			try {
				const { data: { data: place = null } } = await axios.post(
						`${endpoints.update}${this.pass?.id ? this.pass.id : editingPlace.id}`,
						{ ...(new BwPlace(editingPlace)), categories: editingPlace.categories }
				);

				// splice the response place back in
				localPlaces.splice(editingPlaceIdx, 1, new Place(place));
			}
			catch (error) {
				this.errorDescription = error?.response?.data?.message || 'Error adding place';
			}
			finally {
				this.cancelEditPlace();
			}
		},

		async doRemoveContact() {
			const {
				removingContact,
				removingContactIdx,
				editingPlace,
				editingPlaceIdx,
				localPlaces
			} = this;
			this.isSaving = true;

			const url = removingContact.email
					? `/venues/${editingPlace.id}/users/${removingContact.id}`
					: `/venues/${editingPlace.id}/contacts/${removingContact.id}`;

			try {
				await axios.delete(url);

				localPlaces[editingPlaceIdx].merchants.splice(removingContactIdx, 1);
				editingPlace.merchants.splice(removingContactIdx, 1);
			}
			catch (error) {
				this.errorDescription = error?.response?.data?.message || 'Error removing contact';
			}
			finally {
				this.cancelRemoveContact();
			}
		},

		async doRemovePlace() {
			const { removingPlace, removingPlaceIdx } = this;
			this.isSaving = true;

			const url = `${this.endpoints.remove}${this.pass?.id
					? this.pass.id
					: removingPlace.id}`;

			try {
				await axios.post(url, { id: removingPlace.id });
				window.EventBus.$emit('system-messages', [
					{ message: 'Place removed', messageType: 'success' }
				]);
				this.localPlaces.splice(removingPlaceIdx, 1);
				this.cancelRemovePlace();
			}
			catch (error) {
				window.EventBus.$emit('system-messages', [
					{ message: 'Error removing place', messageType: 'error'}
				]);
				console.error('Error removing place:', error);
			}
			finally {
				// update side menu
				this.updateSecondaryMenu();
			}
		},

		editContact(contact, idx) {
			const names = helpers.extractNames(contact);
			contact = { ...contact, ...names };
			this.editingContactIdx = idx;
			this.editingContact = cloneDeep(contact);
		},

		async editPlace({ place }, recentlyAdded = false) {

			// Get all place details
			try {
				const url = `${this.endpoints.getVenue}${place.id}`;
				const { data: { data: updatedPlace = null } } = await axios.get(url);
				const idx = this.localPlaces.findIndex(localPlace => localPlace.id === place.id);

				this.localPlaces.splice(idx, 1, new Place(updatedPlace));

				// Transform loaded categories if necessary
				const { categories } = updatedPlace;
				if(categories.length && helpers.isObject(categories[0])) {
					this.localPlaces[idx].categories = categories.map(cat => cat.id);
				}

				this.editingPlace = cloneDeep(this.localPlaces[idx]);
				this.editingPlace.recentlyAdded = recentlyAdded;
				this.editingPlaceIdx = idx;

				this.editingPlace.checkIns.forEach(checkIn => {
					checkIn.redemption_radius = checkIn.redemption_radius || this.pass.redemption_radius || 0.25;
					checkIn.showBlackOut = !!checkIn.redemption_blackout_dates.length;
					checkIn.checkInIdx = this.getLanguageTagIndex('CHECKIN', checkIn);
					checkIn.checkedInIdx = this.getLanguageTagIndex('CHECKEDIN', checkIn);
				});

				if (this.headerIndex === -1) {
					this.editingPlace.media.push(
						{
							location: 'Header',
							name: 'Header',
							url: '/images/placehold.co.png'
						}
					);
				};

				const video = this.editingPlace.media.find(x => x.location === 'video');
				this.$set(
					this.editingPlace,
					'video',
					video ?? { ...emptyVideo }
				);
				this.formIsValid.video = true;

				const audio = this.editingPlace.media.find(x => x.location === 'audio');
				this.$set(
					this.editingPlace,
					'audio',
					audio ?? { ...emptyAudio }
				);

				// vue-tel-input-vuetify
				//
				// we must put a validation trigger inside nextTick so that the field is correctly
				// validated after initialization.
				this.$nextTick(() => {
					if(this.$refs.placePhone)
						this.$refs.placePhone.$refs.input.validate();

					if (this.$refs.editPlaceForm)
						this.$refs.editPlaceForm.validate();
				});
			}
			catch (error) {
				console.error('Error retrieving place:', error);
			}

		},

		exit() {
			window.location.href = `/partner-portal/${this.partner.id}/passes`;
		},

		getAddressDetails(address) {
			if(!address) {
				this.addressSuggestions = [];
				return;
			}

			const service = new window.google.maps.places.PlacesService(document.createElement('div'));
			service.getDetails({ placeId: address.place_id }, (result, status) => {
				if(status === window.google.maps.places.PlacesServiceStatus.OK) {
					this.editingPlace = {
						...this.editingPlace,
						formatted_address: result.formatted_address,
						formatted_phone_number: this.editingPlace.formatted_phone_number || result.formatted_phone_number,
						geometry: {
							location: {
								lat: result.geometry.location.lat(),
								lng: result.geometry.location.lng()
							}
						},
						types: ['point_of_interest']
					};
				}
				else {
					console.error('Error fetching place details:', status);
				}
			});
		},

		getCharacterCount(str) {
			return helpers.getCharacterCount(str);
		},

		getLanguageTagIndex(tag, { languageTags }) {
			return languageTags.findIndex(({ tag: t }) => t === tag);
		},

		getPlacePredictions(val, types = ['establishment']) {

			return new Promise((resolve, reject) => {
				const service = new window.google.maps.places.AutocompleteService();

				const settings = {
					input: val,
					types: types,
					strictBounds: false
				};

				// 28.372436500000  /  -81.514155900000

				// Partner lat/lng if we have it - or user if we don't
				const { lat, lng } = this.initialLocation;

				// ONLY set bounds if the partner has a lat/lng
				if(!isNaN(lat) && !isNaN(lng)) {

					/*
					 * Let's restrict search priority to a 10x10 mile rectangle but then allow
					 * searching outside the bounds by setting strictBounds: false
					 */

					// Latitude Adjustment
					const latAdjustment = 10 / 69; // 1º of lat/lng is approximately 69 miles

					// Longitude Adjustment (varies by latitude)
					const lngAdjustment = latAdjustment / Math.cos(lat * Math.PI / 180);

					const swCorner = new window.google.maps.LatLng(lat - latAdjustment, lng - lngAdjustment);
					const neCorner = new window.google.maps.LatLng(lat + latAdjustment, lng + lngAdjustment);

					settings.bounds = new window.google.maps.LatLngBounds(swCorner, neCorner);
				}

				// We use a blank country for testing and demos in all countries.
				if(this.partner.country) {
					settings.componentRestrictions = { country: this.partner.country };
				}

				// TODO: Update the restrictions to match the partner's country
				service.getPlacePredictions(settings, (predictions, status) => {
					if(status === window.google.maps.places.PlacesServiceStatus.OK) {
						resolve(predictions);
					}
					else {
						reject(status);
					}
				});
			});
		},

		isContactPhoneValid({ number, valid }) {
			// Only flip the form validity to false if NOT valid, do nothing on valid = true
			if(!valid && number?.input) this.formIsValid.editContact = false;

			// For field validation rule
			this.editingContactPhoneIsValid = valid;
		},

		isPlacePhoneValid({ number, valid }) {
			// Only flip the form validity to false if NOT valid, do nothing on valid = true
			if(!valid && number?.input) this.formIsValid.editPlace = false;

			// For field validation rule
			this.editingPlacePhoneIsValid = valid;
		},

		isManuallyAddedPoi(place) {
			return place.types.length === 1 && place.types[0] === 'point_of_interest';
		},

		next() {
			this.isSaving = true;
			window.location.href = `/partner-portal/${this.partner.id}/passes/${this.pass.id}/inventory`;
		},

		onAudioDelete() {
			this.$set( this.editingPlace, 'audio', { ...emptyAudio });
		},

		onEditingPlaceRemoveImage() {
			const { media: removeMedia } = this.editingPlace;
			const { media: placeholderMedia } = new Place();

			this.editingPlace.removeMedia = removeMedia;

			this.$set(this.editingPlace, 'media', placeholderMedia);
		},

		onEditingPlaceRemoveDealVenueImage() {
			const { media: removeMedia } = this.editingPlace.deal_venue;

			this.$set(this.editingPlace.deal_venue.media, 0, {
				...(new PlaceholderMedia()),
				id: this.editingPlace.deal_venue.media[0]?.id,
				flag: 'delete'
			});
		},

		onVideoDelete() {
			this.$set( this.editingPlace, 'video', { ...emptyVideo });
		},

		phoneKeyEvent(e) {
			const validCharsRegex = new RegExp(/^[0-9 +\-()]*$/);

			// Allow Control+V or Command+V for pasting
			const isPasteOperation = (e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey);

			if(!validCharsRegex.test(e.key) && ![
				'ArrowLeft',
				'ArrowRight',
				'Backspace',
				'Delete',
				'Tab'
			].includes(e.key) && !isPasteOperation) {
				e.preventDefault();
			}
		},

		removeBonusCheckIn(idx) {
			const { editingPlace: { checkIns }, showAdvancedBonusCheckins } = this;

			checkIns.splice(idx, 1);
			showAdvancedBonusCheckins.splice(idx, 1);
		},

		removeContact(contact, idx) {
			this.removingContact = contact;
			this.removingContactIdx = idx;
		},

		async removePlace({ place, idx }) {
			this.isSaving = true;
			this.removingPlace = place;
			if (!this.pass.id) {
				this.removingPlace.passes = (await axios.get(this.endpoints.getPasses + place.id)).data || [];
			}
			this.removingPlaceIdx = idx;
			this.isSaving = false;
		},

		resetVideo(d) {
			const {
					editingPlace,
					editingPlace: { video: { host } },
					videoIndex
			} = this;

			editingPlace.video = {
				caption: '',
				host,
				location: 'video',
				url: ''
			};

			if (videoIndex !== -1) {
				editingPlace.media.splice(this.videoIndex, 1);
			}
		},

		async saveDefaultCheckIn() {
			this.isSaving = true;
			let url = `/partner-portal/${this.partner.id}/passes/save-pass/${this.pass.id || ''}`;

			try {
				const response = await axios.put(url, this.localPass);
				this.defaultCheckInTemplateId = this.localPass.pass_settings.default_checkin_template_id;
				this.dialog.setDefaultCheckInTemplateId = false;
				this.isSaving = false;
				this.addCheckIn();
			}
			catch (error) {
				this.isSaving = false;
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			}
		},

		async saveSort(places) {
			if (!this.pass.id) return;

			this.localPlaces = places || this.localPlaces;

			const venues = this.localPlaces.map(
				({ id }, idx) => {
					return { id, sort_order: idx };
				}
			);

			this.isSaving = true;

			const url = `/partner-portal/${this.partner.id}/passes/update-pass-venues/${this.pass.id}`;

			try {
				await axios.put(url, { venues });
				this.isSaving = false;
			}
			catch (error) {
				this.isSaving = false;
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			}
		},

		async searchAddresses(val) {
			try {
				const predictions = await this.getPlacePredictions(val, ['address']);
				this.addressSuggestions = predictions.filter(
						(prediction) =>
								!this.localPlaces.some((place) => place.place_id === prediction.place_id)
				);
			}
			catch (error) {
				console.error("Error fetching predictions:", error);
				// Handle error here, e.g., show an error message to the user
			}
		},

		toggleAdvancedBonusCheckin(idx) {
			this.$set(this.showAdvancedBonusCheckins, idx, !this.showAdvancedBonusCheckins[idx]);
		},

		updateEditingLocation(center) {
			const place = this.editingPlace;
			place.geometry.location = {
				lat: center.lat,
				lng: center.lng
			};
		},

		updateSecondaryMenu() {
			window.EventBus.$emit('get-updated-menu', {
				portal: 'partner-portal',
				location: 'passes',
				partnerId: this.partner.id,
				dealId: this.pass.id,
				method: 'places'
			});
		},

		validateContactEmail() {
			this.$refs.contactEmail.validate();
		},

		validateContactPhone() {
			this.$refs.contactPhone.$children[1].validate();
		},

		updateBlackoutDates(idx) {
			const checkIn = this.editingPlace.checkIns[idx];
		},

		updateCheckInType(idx) {
			const checkIn = this.editingPlace.checkIns[idx];
			const template = this.checkInTemplates.find(template => template.id === checkIn.template_id)

			checkIn.redemption_validation = this.checkInTemplates
							.find(template => template.id === checkIn.template_id)
							?.redemption_validation || '';
		}
	},
	watch: {
		searchAddress: debounce(function (val) {
			if(val)
				this.searchAddresses(val);
		}, 300),
		'editingPlace.name': {
			handler(newValue) {
				if(newValue && this.editingPlace.formatted_address) this.disableAutoGeneratingDescription = false;
			}
		},
		'editingPlace.formatted_address': {
			handler(newValue) {
				if(newValue && this.editingPlace.name) this.disableAutoGeneratingDescription = false;
			}
		},
		'showAdvancedMedia': {
			handler(newValue) {
				if (newValue) {
					this.$nextTick(() => {
						this.$refs.videoForm.validate();
					});
				}
			}
 		}
	}
}

function Contact(venue, roleId) {
	this.first_name = null;
	this.last_name = null;
	this.email = null;
	this.phone = null;
	this.secondary_phone = null;
	this.role_id = roleId;

	// this.returnMatch = true;
	this.require_new_password = 1;

	this.password = Array.from({ length: 8 }, () => {
		const str = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$';
		return str.charAt(Math.floor(Math.random() * str.length));
	}).join('');

	this.venues = [venue.id];
};

</script>
<style scoped>
.no-bullets {
	list-style-type: none;
	padding-left: 0;
}
</style>

<style lang="scss" scoped>
::v-deep .v-slide-group__next,
::v-deep .v-slide-group__prev {
	display: none !important;
}
</style>
